import React, { useMemo, useState } from 'react';

import { Money } from '@betterment/core';
import { Box, HStack, Spinner, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ParentSize } from '@visx/responsive';

import { PerformancePointHighlightSection } from './components/highlight-section/PerformancePointHighlightSection';
import { PerformanceHighlightSection } from './components/PerformanceHighlightSection';
import { DateOptionToggleGroup } from './DateOptionToggleGroup';
import { DirectTradingAccountSecurityPricePerformanceQuery } from './graphql/directTradingAccountSecurityPricePerformance.generated';
import { type MarketScheduleType, isMarketScheduleOpen } from './marketSchedule';
import { PerformanceChart } from './PerformanceChart';
import { PerformanceDisclosureDialog } from './PerformanceDisclosureDialog';
import { type PricePointType, emptyChartDataForDate, intradayPricePointsForTimeRange } from './pricePoints';
import { isDateRangeOptionToday, timePeriods } from './timePeriods';

import { DateRangeOption } from '../../../../../graphql/schema';
import { PlaceholderPerformanceChart } from '../../../../../Shared/components-v2/placeholder-performance-chart/PlaceholderPerformanceChart';

const overlayStyles = css.raw({
  bg: 'background.surface',
  opacity: '0.4',
});

const centeredSpinnerStyles = css.raw({
  position: 'absolute',
  top: '50%',
  left: '50%',
  translate: '-50% -50%',
  zIndex: '1',
});

type LatestPriceType =
  DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['statistics']['latestPrice'];
type ReturnsType =
  DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['pricePerformance']['returns'];

type InitialPricePointForMarketSchedule = {
  latestPrice: LatestPriceType;
  marketSchedule: DirectTradingAccountSecurityPricePerformanceQuery['marketSchedule'];
  returns: ReturnsType;
};

const initialPricePointForMarketSchedule = ({
  latestPrice,
  marketSchedule,
  returns,
}: InitialPricePointForMarketSchedule) => {
  const record: FinancialSecurityPricePerformancePricePointType = {
    __typename: 'FinancialSecurityPricePerformancePricePoint' as const,
    dateTime: new Date(marketSchedule.openAt!),
    amount: latestPrice.amount,
    returns: {
      __typename: 'FinancialSecurityPriceReturns' as const,
      amount: returns.amount,
      percentage: returns.percentage,
    },
  };

  return record;
};

type FinancialSecurityPricePerformancePricePointType =
  DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['pricePerformance']['timeSeries'][number];

type PerformanceSectionProps = {
  latestPrice: DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['statistics']['latestPrice'];
  returns: DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['pricePerformance']['returns'];
  timeSeries: FinancialSecurityPricePerformancePricePointType[];
  marketSchedule: DirectTradingAccountSecurityPricePerformanceQuery['marketSchedule'];
  dateRangeOption: DateRangeOption;
  onDateRangeChange: (option: DateRangeOption) => void;
  currentDate: Date;
  isLoading?: boolean;
};

export const PerformanceSection: React.FC<PerformanceSectionProps> = ({
  returns,
  latestPrice,
  timeSeries,
  marketSchedule,
  dateRangeOption,
  onDateRangeChange,
  currentDate,
  isLoading = false,
}) => {
  const [highlight, setHighlight] = useState<PricePointType | null>(null);

  const { percentage: latestReturnsPercentage, amount: latestReturnsAmount } = returns;
  const { amount: latestPriceAmount, formattedCurrentAsOf: latestPriceCurrentAsOf } = latestPrice;

  const marketScheduleModel = useMemo(
    (): MarketScheduleType => ({
      date: marketSchedule.date,
      openAt: marketSchedule.openAt,
      closeAt: marketSchedule.closeAt,
      nextBusinessDay:
        marketSchedule.nextBusinessDay != null
          ? {
              openAt: marketSchedule.nextBusinessDay.openAt,
            }
          : undefined,
    }),
    [marketSchedule]
  );

  const shouldShowDateToggle = timeSeries.length > 0;
  const isDateRangeOptionTodayValue = isDateRangeOptionToday(dateRangeOption);
  const isMarketScheduleOpenValue = isMarketScheduleOpen(marketScheduleModel);
  const emptyChartData = emptyChartDataForDate({ date: currentDate });

  const timeSeriesCollection = useMemo(
    () =>
      timeSeries.map((record: FinancialSecurityPricePerformancePricePointType) => {
        return {
          __typename: record.__typename,
          dateTime: new Date(record.dateTime),
          amount: Money(record.amount.cents),
          returns: {
            __typename: record.returns.__typename,
            amount: Money(record.returns.amount.cents),
            percentage: record.returns.percentage,
          },
        };
      }),
    [timeSeries]
  );

  const chartData = useMemo((): PricePointType[] => {
    if (isDateRangeOptionTodayValue) {
      if (!isMarketScheduleOpenValue) return [];

      const initialPricePoint = initialPricePointForMarketSchedule({
        latestPrice,
        marketSchedule,
        returns,
      });

      let resolvedTimeSeries = timeSeriesCollection;

      const existingStartPricePoint = timeSeries.find(pricePoint => {
        return new Date(pricePoint.dateTime).getTime() === initialPricePoint.dateTime.getTime();
      });

      if (existingStartPricePoint === undefined) {
        resolvedTimeSeries = [initialPricePoint, ...timeSeriesCollection];
      }

      return intradayPricePointsForTimeRange({
        startTime: new Date(marketScheduleModel.openAt!),
        endTime: new Date(marketScheduleModel.closeAt!),
        pricePoints: resolvedTimeSeries,
      });
    }

    if (timeSeriesCollection.length > 0) return timeSeriesCollection;
    if (latestPriceAmount.cents === 0) return [];
    return emptyChartData;
  }, [
    isDateRangeOptionTodayValue,
    isMarketScheduleOpenValue,
    latestPrice,
    marketSchedule,
    marketScheduleModel,
    returns,
    timeSeries,
    timeSeriesCollection,
    latestPriceAmount.cents,
    emptyChartData,
  ]);

  return (
    <VStack css={{ gap: 'space.300' }}>
      {highlight !== null ? (
        <PerformancePointHighlightSection
          highlight={highlight}
          latestPriceAmount={latestPriceAmount}
          latestReturnsAmount={latestReturnsAmount}
          latestReturnsPercentage={latestReturnsPercentage}
          dateRangeOption={dateRangeOption}
        />
      ) : (
        <PerformanceHighlightSection
          latestPriceAmount={latestPriceAmount}
          latestReturnsAmount={latestReturnsAmount}
          latestReturnsPercentage={latestReturnsPercentage}
          marketSchedule={marketScheduleModel}
          dateRangeOption={dateRangeOption}
          currentDate={currentDate}
        />
      )}

      <Box>
        {isLoading && <Spinner css={centeredSpinnerStyles} />}
        <Box css={{ ...(isLoading && overlayStyles), width: '100%', height: '100%' }}>
          <ParentSize>
            {({ width }) =>
              chartData.length > 0 ? (
                <PerformanceChart
                  width={width}
                  height={200}
                  data={chartData}
                  dateRangeOption={dateRangeOption}
                  disabled={isLoading}
                  onHighlightChange={setHighlight}
                />
              ) : (
                <PlaceholderPerformanceChart width={width} height={200} variant="zero-state" />
              )
            }
          </ParentSize>
        </Box>
      </Box>

      {shouldShowDateToggle && (
        <HStack css={{ justifyContent: 'center' }}>
          <DateOptionToggleGroup
            value={dateRangeOption}
            onValueChange={(val: DateRangeOption) => val != null && onDateRangeChange(val)}
          >
            {timePeriods.map(({ label, value: optionValue }) => (
              <DateOptionToggleGroup.Item key={optionValue} value={optionValue}>
                {label}
              </DateOptionToggleGroup.Item>
            ))}
          </DateOptionToggleGroup>
        </HStack>
      )}

      <Text variant="text25" color="text.weak">
        Price as of {latestPriceCurrentAsOf}. See <PerformanceDisclosureDialog />
      </Text>
    </VStack>
  );
};
